import React, {
  useCallback,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { createRoot } from "react-dom/client";
import { useEditor, EditorContent } from "@tiptap/react";
import { Extension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { Placeholder } from "@tiptap/extension-placeholder";
import {
  PlaceholderNode,
  createPlaceholderSuggestionExtension,
} from "./PlaceholderExtension";
import PlaceholderList from "./PlaceholderList";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "../../Theme.js";
import useThemeStore, { resolveMode } from "../../stores/useThemeStore";
import tippy from "tippy.js";
import "tippy.js/dist/tippy.css";
import "./ChatMessageInput.css";

export const toAgentHandle = (name, id) => {
  const normalized = (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
  return normalized || `agent-${(id || "").slice(0, 8)}`;
};

const getPlainTextWithMentions = (editor) => {
  const json = editor.getJSON();
  let result = "";
  const processNode = (node) => {
    if (node.type === "text") {
      result += node.text || "";
    } else if (node.type === "placeholderNode" && node.attrs?.prefix === "@") {
      result += `@${node.attrs.key}`;
    } else if (node.type === "hardBreak") {
      result += "\n";
    } else if (node.type === "paragraph") {
      if (result.length > 0 && !result.endsWith("\n")) result += "\n";
      (node.content || []).forEach(processNode);
    } else if (node.content) {
      (node.content || []).forEach(processNode);
    }
  };
  (json.content || []).forEach(processNode);
  return result.trim();
};

const createMentionSuggestion = (getAgents) => {
  let popup = null;
  let component = null;

  return {
    char: "@",
    allowSpaces: false,
    startOfLine: false,

    items: ({ query }) => {
      const agents = getAgents();
      if (!agents || !Array.isArray(agents)) return [];
      return agents
        .filter((item) => item.key.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 8);
    },

    render: () => {
      let listRef = { current: null };

      return {
        onStart: (props) => {
          const container = document.createElement("div");
          container.className = "chat-mention-popup";

          const root = createRoot(container);

          const getPopupTheme = () => {
            const mode = resolveMode(useThemeStore.getState().preference);
            return createAppTheme(mode);
          };

          const updateList = (items, command) => {
            root.render(
              <ThemeProvider theme={getPopupTheme()}>
                <PlaceholderList
                  ref={listRef}
                  items={items}
                  command={command}
                  triggerChar="@"
                />
              </ThemeProvider>
            );
          };

          updateList(props.items, props.command);

          popup = tippy("body", {
            getReferenceClientRect: props.clientRect,
            appendTo: () => document.body,
            content: container,
            showOnCreate: true,
            interactive: true,
            trigger: "manual",
            placement: "top-start",
            maxWidth: 320,
            zIndex: 99999,
          })[0];

          component = { root, container, updateList, listRef };
        },

        onUpdate: (props) => {
          if (component) component.updateList(props.items, props.command);
          if (popup)
            popup.setProps({ getReferenceClientRect: props.clientRect });
        },

        onKeyDown: (props) => {
          if (props.event.key === "Escape") {
            popup?.hide();
            return true;
          }
          if (component?.listRef?.current?.onKeyDown) {
            return component.listRef.current.onKeyDown(props);
          }
          return false;
        },

        onExit: () => {
          if (popup) {
            popup.destroy();
            popup = null;
          }
          if (component) {
            component.root.unmount();
            component = null;
          }
        },
      };
    },

    command: ({ editor, range, props }) => {
      if (popup) {
        popup.destroy();
        popup = null;
      }
      if (component) {
        component.root.unmount();
        component = null;
      }

      editor
        .chain()
        .focus()
        .deleteRange(range)
        .insertContent({
          type: "placeholderNode",
          attrs: {
            prefix: "@",
            key: props.key,
            type: "agent",
            isValid: true,
            id: props.id,
          },
        })
        .insertContent(" ")
        .run();
    },
  };
};

/**
 * Lightweight TipTap-based chat message input with @ agent mention support.
 *
 * Props:
 *  - onSend(text)   Called with plain text (mentions as @handle) on Enter
 *  - onChange(empty) Called when empty state changes
 *  - placeholder     Placeholder string
 *  - disabled        Disables editing and sending
 *  - agents          Array of { key, id, description } for @ suggestions
 *  - autoFocus       Focus editor on mount
 *  - onReady()       Called when the editor ref API is ready
 *
 * Ref methods: focus(), send(), clear(), isEmpty(), getText(), editor
 */
const ChatMessageInput = forwardRef(
  (
    {
      onSend,
      onChange,
      placeholder = "Send a message...",
      disabled = false,
      agents = [],
      autoFocus = false,
      onReady,
    },
    ref
  ) => {
    const agentsRef = useRef(agents);
    useEffect(() => {
      agentsRef.current = agents;
    }, [agents]);

    const getAgents = useCallback(() => agentsRef.current || [], []);

    const onSendRef = useRef(onSend);
    useEffect(() => {
      onSendRef.current = onSend;
    }, [onSend]);

    const disabledRef = useRef(disabled);
    useEffect(() => {
      disabledRef.current = disabled;
    }, [disabled]);

    const onChangeRef = useRef(onChange);
    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const hasMentions = agents && agents.length > 0;

    const mentionExtension = useMemo(() => {
      if (!hasMentions) return null;
      const ext = createPlaceholderSuggestionExtension("@");
      return ext.configure({
        suggestion: createMentionSuggestion(getAgents),
      });
    }, [hasMentions, getAgents]);

    const EnterToSend = useMemo(
      () =>
        Extension.create({
          name: "enterToSend",
          priority: 1000,
          addKeyboardShortcuts() {
            return {
              Enter: ({ editor }) => {
                if (disabledRef.current) return true;
                const text = getPlainTextWithMentions(editor);
                if (text && onSendRef.current) {
                  onSendRef.current(text);
                  setTimeout(() => editor.commands.clearContent(true), 0);
                }
                return true;
              },
              "Shift-Enter": ({ editor }) => {
                return editor.commands.setHardBreak();
              },
            };
          },
        }),
      []
    );

    const extensions = useMemo(() => {
      const exts = [
        StarterKit.configure({
          history: true,
          heading: false,
          blockquote: false,
          codeBlock: false,
          horizontalRule: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
        }),
        PlaceholderNode,
        Placeholder.configure({ placeholder }),
        EnterToSend,
      ];
      if (mentionExtension) exts.splice(2, 0, mentionExtension);
      return exts;
    }, [placeholder, mentionExtension, EnterToSend]);

    const editor = useEditor({
      extensions,
      editable: !disabled,
      autofocus: autoFocus,
      onUpdate: ({ editor: ed }) => {
        if (onChangeRef.current) onChangeRef.current(ed.isEmpty);
      },
    });

    useEffect(() => {
      if (editor) editor.setEditable(!disabled);
    }, [disabled, editor]);

    useEffect(() => {
      if (editor && onReady) {
        onReady();
      }
    }, [editor, onReady]);

    const doSend = useCallback(() => {
      if (!editor || disabledRef.current) return;
      const text = getPlainTextWithMentions(editor);
      if (text && onSendRef.current) {
        onSendRef.current(text);
        editor.commands.clearContent(true);
        editor.commands.focus();
      }
    }, [editor]);

    const setText = useCallback(
      (text) => {
        if (!editor) return;
        const lines = String(text || "").split("\n");
        editor.commands.setContent({
          type: "doc",
          content: lines.map((line) => ({
            type: "paragraph",
            content: line ? [{ type: "text", text: line }] : [],
          })),
        });
        editor.commands.focus("end");
        if (onChangeRef.current) onChangeRef.current(editor.isEmpty);
      },
      [editor]
    );

    useImperativeHandle(
      ref,
      () => ({
        focus: () => editor?.commands.focus(),
        send: doSend,
        clear: () => editor?.commands.clearContent(true),
        setText,
        getText: () => (editor ? getPlainTextWithMentions(editor) : ""),
        isEmpty: () => (editor ? editor.isEmpty : true),
        editor,
      }),
      [editor, doSend, setText]
    );

    if (!editor) return null;

    return (
      <div className={`chat-message-input ${disabled ? "disabled" : ""}`}>
        <EditorContent editor={editor} />
      </div>
    );
  }
);

ChatMessageInput.displayName = "ChatMessageInput";

export default ChatMessageInput;
